"use client";

import { IncomingOrderDialog } from "@shared/src/components/common/dialogs/IncomingOrderDialog";
import { useQuery } from "@tanstack/react-query";
import { AD_QUERY_KEY } from "@shared/src/lib/queryKeys";
import { useRouter, useSearchParams } from "next/navigation";
import { OrderStats, OrderType, QuickTrade } from "@sdk/shared/types/order.types";
import type { QuickTradeNotificationData } from "@sdk/notification/types";
import { EnrichedAd } from "@sdk/shared/types/ads.types";
import { ORDER_PAGE } from "@shared/src/config/page-links";
import { useNotificationStore } from "@shared/src/stores/useNotificationStore";
import { ixToast } from "@shared/src/utils/ixToast";
import { ensureCorrectNetwork } from "@sdk/orderExecutor/functions";
import { useModalStore } from "@shared/src/stores/useModalStore";
import { useHostStore } from "@shared/src/stores/useHostStore";
import { IncompatibleNetworkError, NetworkError } from "@sdk/errors";
import { useEffect, useState } from "react";
import { ProcessedMerchantStats, processMerchantStats } from "@sdk/shared/lib/merchant-stats";
import { useMerchantStats } from "@shared/src/hooks/useMerchantStats";

const isValidQuickTrade = (value: QuickTradeNotificationData["quickTrade"]): value is QuickTrade => {
  return !!value && typeof value.id === "string" && value.id.length > 0;
};

// Module-level function to extract QuickTrade from search params
export function extractQuickTradeFromParams(searchParams: URLSearchParams): {
  incomingOrder: QuickTrade;
  adId: string;
} {
  return {
    incomingOrder: {
      id: searchParams.get("id") || "",
      orderHash: searchParams.get("orderHash") || "",
      status: (searchParams.get("status") as "pending" | "accepted" | "expired") || "pending",
      trader: (searchParams.get("trader") as `0x${string}`) || "",
      quantity: Number(searchParams.get("quantity")) || 0,
      currency: searchParams.get("currency") || "",
      tokenAddress: (searchParams.get("tokenAddress") as `0x${string}`) || "",
      paymentMethod: (searchParams.get("paymentMethod") as "bank" | "card" | "crypto") || "bank",
      traderChain: Number(searchParams.get("traderChain")) || 0,
      priceLimit: Number(searchParams.get("priceLimit")) || 0,
      type: (searchParams.get("type") as "buy" | "sell") || "buy",
      paymentConfig: Number(searchParams.get("paymentConfig")),
      lastContactedTime: Number(searchParams.get("lastContacted")) || 0,
      currentAdId: searchParams.get("adId") ? Number(searchParams.get("currentAdId")) : null,
    },
    adId: searchParams.get("adId") || "",
  };
}

// Module-level function to create URL route from QuickTrade params
export function createQuickTradeIncomingSearchParams(params: QuickTrade, adId: string): string {
  const searchParams = new URLSearchParams({
    id: params.id,
    orderHash: params.orderHash,
    status: params.status,
    trader: params.trader,
    quantity: params.quantity.toString(),
    currency: params.currency,
    tokenAddress: params.tokenAddress,
    paymentMethod: params.paymentMethod,
    traderChain: params.traderChain.toString(),
    priceLimit: params.priceLimit.toString(),
    type: params.type,
    paymentConfig: params.paymentConfig?.toString() || "",
    adId: adId,
  });

  return searchParams.toString();
}

export default function IncomingOrderView() {
  const quickTradeNotification = useNotificationStore(state => state.quickTradeNotification);
  const setQuickTradeNotification = useNotificationStore(state => state.setQuickTradeNotification);
  const sdk = useHostStore(state => state.sdk);
  const router = useRouter();
  const rawQuickTrade = quickTradeNotification?.quickTrade;
  const adId = quickTradeNotification?.adId;
  const quickTrade = isValidQuickTrade(rawQuickTrade) ? rawQuickTrade : null;
  const [isLoading, setIsLoading] = useState(false);
  const searchParams = useSearchParams();

  const isMerchant = quickTrade?.trader !== sdk?.auth.getAddress();

  const { data: merchantAd } = useQuery({
    queryKey: AD_QUERY_KEY(quickTrade?.id || ""),
    queryFn: async () => {
      const res = await sdk?.ads.getAdById(adId || "");
      if (res?.error) throw res.error;
      return res?.data;
    },
    enabled: !!adId && isMerchant,
  });

  const { stats } = useMerchantStats(quickTrade?.trader || "");

  useEffect(() => {
    const fetchQuickTrade = async () => {
      setIsLoading(true);
      const { data } = (await sdk?.orders.getUserQuickTrade()) || {};
      console.log({ quicktradeData: data });
      if (data) {
        setQuickTradeNotification(data);
      }
    };

    if (searchParams.get("quick-trade")) fetchQuickTrade().finally(() => setIsLoading(false));
  }, [searchParams]);

  const closeDialog = () => {
    router.replace("?", { scroll: false });
  };

  const onAccept = async () => {
    if (!quickTrade || !quickTrade.id || !sdk?.config.networks || !merchantAd) {
      console.error("missing data", { quickTrade, merchantAd, networks: sdk?.config.networks });
      throw "missing data";
    }

    const toastId = ixToast.loading("Processing", { title: "Transaction Pending" });

    try {
      const data = await sdk?.executeOrder({
        action: "acceptQuickTradeByMerchant",
        quickTrade,
        ad: merchantAd,
        amount: undefined,
        orderData: undefined,
        paymentConfig: quickTrade.paymentConfig,
      });

      if (data && data.data) {
        router.push(ORDER_PAGE(data.data.id));
        setQuickTradeNotification(null);

        // Show success toast
        ixToast.success("Order has been successfully accepted", { title: "Order Accepted" });
      } else throw data.error;
    } catch (error: any) {
      console.error("Error accepting order:", error);
      if (error instanceof IncompatibleNetworkError) {
        ixToast.error({ title: "Network mismatch" });
        useModalStore.getState().showNetworkMismatchModal(error.data?.expectedChainId || 0);
        return;
      }

      // incomingorderview.tsx:124 Error accepting order: BaseError: {"error":"{\"code\":16, \"message\":\"expired api key publicKey=02454d3acdbec9904e2c1e209eb9e28861104b08117784820584638240faa7b515\", \"details\":[{\"@type\":\"type.googleapis.com/errors.v1.TurnkeyErrorDetail\", \"turnkeyErrorCode\":\"API_KEY_EXPIRED\"}], \"turnkeyErrorCode\":\"API_KEY_EXPIRED\"}"}
      ixToast.error(error.message || "Failed to accept the order", { title: "Error" });
      setQuickTradeNotification(null);
      // Show error toast
    } finally {
      // Dismiss loading toast
      toastId.dismiss();
    }
  };

  const onReject = async () => {
    if (!quickTrade || !quickTrade.id) return;
    if (!adId) throw new Error("Ad ID is required");
    await sdk?.orders.rejectQuickTrade(quickTrade.id, {
      adId: adId.toString(),
    });
    setQuickTradeNotification(null);
  };

  const isReady = !!merchantAd && !!quickTrade?.id && !!stats;
  if (!isReady) return null;

  console.log("Rendering IncomingOrderDialog", { quickTrade, merchantAd, stats });

  return (
    <IncomingOrderDialog
      open={isReady}
      isLoading={isLoading}
      takerStats={stats}
      onOpenChange={() => {}}
      onAccept={onAccept}
      onReject={onReject}
      quickTrade={quickTrade}
      ad={merchantAd}
    />
  );
}
